const SILENCE_DB_RANGE = -135;
const DEFAULT_DB_RANGE_HIGH = 150;
const DEFAULT_DB_RANGE_LOW = 60;

export type FrequencySamplerOptions = {
  onFrequencyData?: (frequencyData: number[]) => void;
  sampleNumber?: number;
};

export class FrequencySampler {
  public isRunning: boolean = false;

  private options: FrequencySamplerOptions;
  private analyser: AnalyserNode | null = null;
  private dataArray: Float32Array<ArrayBuffer> | null = null;
  private bufferLength: number | null = null;
  private sampleNumber: number;
  private samplingIntervalId: number | null = null;
  private readonly SAMPLING_INTERVAL_MS = 150; // 0.15 seconds (FFT_INTERVAL)
  private dbBoundsUpper: number = DEFAULT_DB_RANGE_HIGH;
  private lastNormalizationDate: number = Date.now();

  constructor(options: FrequencySamplerOptions = {}) {
    this.options = options;
    this.sampleNumber = options.sampleNumber || 4;
  }

  public initialize(analyser: AnalyserNode): void {
    this.analyser = analyser;

    this.analyser.smoothingTimeConstant = 0;
    this.analyser.minDecibels = -60;
    this.analyser.maxDecibels = -10;
    this.analyser.fftSize = 2048;

    this.bufferLength = this.analyser.frequencyBinCount;
    // new Float32Array() always creates ArrayBuffer-backed arrays
    this.dataArray = new Float32Array(
      this.bufferLength,
    ) as any as Float32Array<ArrayBuffer>;

    this.dbBoundsUpper = DEFAULT_DB_RANGE_HIGH;
    this.lastNormalizationDate = Date.now();
  }

  public start(): void {
    if (!this.analyser || !this.dataArray) {
      console.warn("FrequencySampler not initialized");
      return;
    }

    this.stop();

    try {
      this.samplingIntervalId = window.setInterval(() => {
        this.sample();
      }, this.SAMPLING_INTERVAL_MS);
      this.isRunning = true;
    } catch (error) {
      console.error("Failed to start frequency sampling interval:", error);
    }
  }

  public stop(): void {
    this.isRunning = false;
    if (this.samplingIntervalId != null) {
      clearInterval(this.samplingIntervalId);
      this.samplingIntervalId = null;
    }
  }

  public destroy(): void {
    this.stop();
    this.isRunning = false;
    this.analyser = null;
    this.dataArray = null;
    this.bufferLength = null;
  }

  /**
   * Returns normalized frequency data in a number array with the specified sample size set when initialized
   * https://developer.mozilla.org/en-US/docs/Web/API/Web_Audio_API/Visualizations_with_Web_Audio_API
   */
  public getFrequencyData(): number[] {
    if (!this.analyser || !this.dataArray) {
      return [];
    }

    this.analyser.getFloatFrequencyData(this.dataArray);

    if (this.isSilence(this.dataArray)) {
      return Array(this.sampleNumber).fill(0);
    }

    // Split frequency data into different blocks based on the sample size then get the average of each block
    const blockSize = Math.floor(this.dataArray.length / this.sampleNumber);
    const filteredData = [];

    for (let i = 0; i < this.sampleNumber; i++) {
      let blockStart = blockSize * i;
      let sum = 0;
      for (let j = 0; j < blockSize; j++) {
        sum = sum + Math.abs(this.dataArray[blockStart + j]);
      }
      filteredData.push(sum / blockSize);
    }

    // filteredData.reverse();

    // Normalize audio based on the highest frequency bound
    const max = Math.max(...filteredData);
    if (this.lastNormalizationDate + 5000 < Date.now()) {
      this.lastNormalizationDate = Date.now();
      this.dbBoundsUpper = DEFAULT_DB_RANGE_HIGH;
    }

    this.dbBoundsUpper = Math.min(
      DEFAULT_DB_RANGE_HIGH,
      Math.max(this.dbBoundsUpper, max),
    );

    const headRoom = this.dbBoundsUpper - DEFAULT_DB_RANGE_LOW;
    const normalizedData = filteredData.map((n) =>
      Math.min(1.0, Math.max(0, n - DEFAULT_DB_RANGE_LOW) / headRoom),
    );

    return normalizedData;
  }

  private sample(): void {
    if (!this.analyser || !this.dataArray) return;
    const frequencyData = this.getFrequencyData();
    this.options.onFrequencyData?.(frequencyData);
  }

  private isSilence(values: Float32Array): boolean {
    const avg = values.reduce((acc, v) => acc + v, 0) / values.length;

    if (avg < SILENCE_DB_RANGE) {
      return true;
    }

    return values.some((n) => n == Infinity || isNaN(n));
  }
}
